import React, { useCallback, useContext, useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import Pane from 'components/Common/Pane'
import Group from 'components/utils/Group'
import LuxPlusPriceStack from 'luxPlus/components/LuxPlusPriceStack'
import PriceRowValueDiscountWithCaption from 'LuxKit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import PriceRowPriceCaption from 'LuxKit/PricePoints/PriceRowPriceCaption'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getActivePackagesByRoom, getCalendarsToBeFetchedByRoom, getMultiRoomPackageTotals, getIsLoadingByRoom, LoadingStatusByRoom, MultiRoomPackageTotals, PackageOptionViewByRoom } from 'selectors/offerPage/multipleRoomBookingSelectors'
import LoyaltyPointsEarnWithBreakdownModal from 'loyalty/components/Earn/LoyaltyPointsEarnWithBreakdownModal'
import { generateLoyaltyPointsCalculatorBundledFlightOptions, generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import MultiRoomOfferSelectRoomsButton from './MultiRoomOfferSelectRoomsButton'
import MultiRoomOfferAddToCartButton from './MultiRoomOfferAddToCartButton'
import MultiRoomOfferBookButton from './MultiRoomOfferBookButton'
import { getCanRedeemLuxPlusBenefits, getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { EmptyArray, groupBy, nonNullable } from 'lib/array/arrayUtils'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import ContactContext from 'contexts/contactContext'
import GeoContext from 'contexts/geoContext'
import useOptimizelyExperiment from 'hooks/Optimizely/useOptimizelyExperiment'
import { OptimizelyFeatureFlags } from 'constants/optimizely'
import ModalContext from 'contexts/ModalContext'
import RequestAgentCallbackModal from 'supportAssistant/components/RequestAgentCallback/Modals/RequestAgentCallbackModal'
import TextButton from 'LuxKit/Button/TextButton'
import Sheet from 'components/utils/Sheet'
import BodyText from 'LuxKit/Typography/BodyText'
import { initialiseBnblOfferModal } from 'actions/BnblModalActions'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageActions, OfferPagePriceMode, OfferPageRoom } from 'contexts/OfferPage/offerPageStateReducer'
import { getBnblPackageViews } from 'selectors/offerPage/offerPageSelectors'
import { getBnblPackageViewByNumberOfRooms } from 'components/OfferPage/BnblModal/utils'
import { useAppDispatch } from 'hooks/reduxHooks'
import getDatasetFromEvent from 'lib/analytics/getDatasetFromEvent'
import OfferPageBNBLcta from 'components/OfferPage/OfferPageBNBLcta'
import config from 'constants/config'
import { useOfferPageFlightPrice } from 'hooks/useFlightPrice'
import { getPriceDisplayFunction } from 'lib/offer/offerPageUtil'
import getValueOffPercent from 'lib/offer/getValueOffPercent'
import { MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE } from 'constants/content'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Caption from 'LuxKit/Typography/Caption'
import { pluralizeToString } from 'lib/string/pluralize'
import { useIntl } from 'react-intl'

const FooterContainer = styled(Pane)`
  padding: ${rem(20)};

  ${mediaQueryUp.tablet} {
    padding: 0;
  }
`

interface Props {
  offer: App.HotelOffer
  rooms: Array<OfferPageRoom>
  checkIn?: string
  isFlightsSelected: boolean
  multiRoomPackages: PackageOptionViewByRoom
  multiRoomPackageTotals: MultiRoomPackageTotals
  isLoadingByRoom: LoadingStatusByRoom
  calendarsToBeFetchedByRoom: LoadingStatusByRoom
  canRedeemLuxPlusBenefits: boolean
  baseBnblPackageViews: Array<App.OfferPackageOptionView>
  priceMode: OfferPagePriceMode
  priceIsLuxPlus: boolean
  charterBundleRoute?: App.CharterBundleRoute
  duration: number
}

function MultipleRoomsIndividualRoomsFooter({
  offer,
  rooms,
  checkIn,
  isFlightsSelected,
  multiRoomPackages,
  multiRoomPackageTotals,
  isLoadingByRoom,
  calendarsToBeFetchedByRoom,
  canRedeemLuxPlusBenefits,
  baseBnblPackageViews,
  priceMode,
  priceIsLuxPlus,
  charterBundleRoute,
  duration,
}: Props) {
  const intl = useIntl()

  const limitedHoursLabel = intl.formatMessage({
    id: 'WwAw1OMtcL',
    defaultMessage: 'Our team are available 7 days a week from 9am to 7pm',
    description: 'Support availability message for regions without 24/7 support',
  })
  const fullHoursLabel = intl.formatMessage({
    id: '8JPALsyZTP',
    defaultMessage: 'Our team are available 24 hours, 7 days',
    description: 'Support availability message for regions with 24/7 support',
  })

  const {
    hotelPrice,
    hotelMemberPrice,
    hotelValue,
    shouldDisplayValue,
  } = multiRoomPackageTotals

  const { totalFlightPrice: flightPrice, loading: flightPriceLoading } = useOfferPageFlightPrice()

  const missingCharterFlightPrice = !!charterBundleRoute && !flightPrice && !flightPriceLoading

  const hotelPlusFlightsPrice = hotelPrice + (flightPrice ?? 0)
  const hotelPlusFlightsMemberPrice = hotelMemberPrice ? hotelMemberPrice + (flightPrice ?? 0) : hotelMemberPrice
  const hotelPlusFlightsValue = hotelValue + (flightPrice ?? 0)

  const showModal = useContext(ModalContext)
  const currentContact = useContext(ContactContext)
  const { currentRegionCode } = useContext(GeoContext)
  const offerPageDispatch = useOfferPageDispatch()
  const dispatch = useAppDispatch()

  const callbacksFeatureEnabled = !!useOptimizelyExperiment(OptimizelyFeatureFlags.hotelsAndVillasRequestAgentCallbackEnabled)

  const hasFlights = !!flightPrice && !offer.flightCacheDisabled
  const shouldPreventMultipleRoomsBooking = rooms.length > 2 && canRedeemLuxPlusBenefits && hotelMemberPrice > 0 || missingCharterFlightPrice
  const isCapacityValid = Object.values(multiRoomPackages).every(pkg => pkg?.capacityValid)
  const isAllowBuyNowBookLater = Object.values(multiRoomPackages).every(pkg => pkg?.package.allowBuyNowBookLater)
  const showBNBL = offer.hasBuyNowBookLater && config.accommodation.hasBnbl && isCapacityValid && isAllowBuyNowBookLater && !charterBundleRoute && !shouldPreventMultipleRoomsBooking

  const isBookNowDisabled = useMemo(() => (
    Object.values(isLoadingByRoom).some(Boolean) ||
    Object.values(calendarsToBeFetchedByRoom).some(Boolean) ||
    Object.values(multiRoomPackages).some(pkg => !pkg)
  ), [isLoadingByRoom, calendarsToBeFetchedByRoom, multiRoomPackages])

  const loyaltyHotelOptions = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    return Object.values(multiRoomPackages)?.map(packageView => (
      generateLoyaltyPointsCalculatorHotelOptions(offer, { packageView, currentRegionCode, nights: packageView?.package?.duration })
    ))
  }, [offer, multiRoomPackages, currentRegionCode])

  const pointsEarnCalculationRequests = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => [
    ...loyaltyHotelOptions,
    generateLoyaltyPointsCalculatorBundledFlightOptions(offer, {
      flightPrice,
      skip: !isFlightsSelected,
    }),
  ], [offer, isFlightsSelected, flightPrice, loyaltyHotelOptions])

  const onCallbackButtonClick = useCallback(() => {
    showModal(<RequestAgentCallbackModal type="HOTELS_AND_VILLAS" />)
  }, [showModal])

  const onOpenBnblModal = useCallback((e: React.MouseEvent<HTMLAnchorElement>) => {
    offerPageDispatch({ type: OfferPageActions.toggleFlightsPrice, isFlightsSelected: false })

    const isGift = getDatasetFromEvent(e, 'bnbltype') === 'gift'
    const selectedPackages = nonNullable(Object.values(multiRoomPackages))
    const groupedPackagesRecords = groupBy(selectedPackages, pkg => pkg.package.uniqueKey)
    const packages = [...groupedPackagesRecords.entries()].map(groupedPackage => {
      const basePackage = baseBnblPackageViews.find(packageView => packageView.package.uniqueKey === groupedPackage[0])!
      const numberOfRooms = groupedPackage[1].length

      return {
        packageView: getBnblPackageViewByNumberOfRooms(basePackage, numberOfRooms),
        numberOfRooms,
      }
    })

    dispatch(initialiseBnblOfferModal(packages, offer.id, isGift))
  }, [dispatch, offerPageDispatch, multiRoomPackages, offer.id, baseBnblPackageViews])

  const convertToDisplay = getPriceDisplayFunction(priceMode, rooms)

  const canShowPrice = hotelPrice > 0 && !missingCharterFlightPrice
  const activeHotelPrice = priceIsLuxPlus ? hotelMemberPrice : hotelPrice
  const discountPercentage = getValueOffPercent(hotelValue, activeHotelPrice)
  const shouldShowValue = shouldDisplayValue && hotelValue > activeHotelPrice
  const shouldShowDiscount = shouldShowValue && !offer.isDiscountPillHidden && discountPercentage > MINIMUM_DISCOUNT_TO_SHOW_PERCENTAGE_BADGE

  return (
    <FooterContainer>
      <Group direction="vertical" gap={20} desktopDirection="horizontal-reverse" desktopHorizontalAlign="space-between" verticalAlign="center">
        <div>
          <Group direction="vertical" gap={20} desktopDirection="horizontal" verticalAlign="center">
            {canShowPrice && <div>
              <PriceRowPriceCaption>
                {/* TODO: i18n — multiple spaced fragments with pluralization and pricing context (💲) */}
                {pluralizeToString('night', duration)}, {pluralizeToString(offer.saleUnit, rooms.length)} {isFlightsSelected && hasFlights ? '+ flights' : ''} from
              </PriceRowPriceCaption>
              <LuxPlusPriceStack
                size="S"
                price={convertToDisplay(isFlightsSelected ? hotelPlusFlightsPrice : hotelPrice)}
                memberPrice={convertToDisplay(isFlightsSelected ? hotelPlusFlightsMemberPrice : hotelMemberPrice)}
                saleUnit={(priceMode === 'perPerson' ? 'person' : 'total') + (isFlightsSelected && hasFlights ? '*' : '')}
                testid="pricing"
                offerConfig={offer.luxPlus}
              />
              {/* TODO: i18n — pricing fragment (💲) */}
              {isFlightsSelected && !flightPrice && <Caption variant="large">+ flights</Caption>}
              {shouldShowValue && <PriceRowValueDiscountWithCaption
                size="M"
                originalValue={convertToDisplay(isFlightsSelected ? hotelPlusFlightsValue : hotelValue)}
                discountPercentage={shouldShowDiscount ? discountPercentage : undefined}
              />}
            </div>}
            <div>

              {!shouldPreventMultipleRoomsBooking && <Group direction="vertical" gap={20}>
                <LoyaltyPointsEarnWithBreakdownModal
                  calculationRequests={pointsEarnCalculationRequests}
                  calculationType={(!checkIn || isFlightsSelected) ? 'estimate' : 'exact'}
                />
                {isFlightsSelected && <MultiRoomOfferSelectRoomsButton size="large" fit="flex" disabled={isBookNowDisabled} flightPrice={flightPrice} />}
                {!isFlightsSelected && <Group direction="horizontal" gap={12} horizontalAlign="stretch">
                  {checkIn && <MultiRoomOfferAddToCartButton size="large" fit="flex" disabled={isBookNowDisabled} />}
                  <MultiRoomOfferBookButton size="large" fit="flex" disabled={isBookNowDisabled} />
                </Group>}
              </Group>}
              {shouldPreventMultipleRoomsBooking && (
                <Sheet background="neutral-seven" padding={20}>
                  <Group direction="vertical" gap={8}>
                    <Group direction="vertical" gap={4}>
                      <BodyText variant="medium" weight="bold">
                        {intl.formatMessage({
                          id: 'N9bY8xN+vd',
                          defaultMessage: 'Ready to book? Call us to complete your booking',
                          description: 'Heading prompting user to call to complete multi-room booking',
                        })}
                      </BodyText>
                      <BodyText variant="medium">
                        {NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) ? limitedHoursLabel : fullHoursLabel}
                      </BodyText>
                    </Group>
                    <TextButton startIcon={<LinePhoneIcon />} kind="primary" href={`tel:${currentContact.defaultContact.number}`} fit="flex">
                      {intl.formatMessage(
                        {
                          id: '9mTKElgQn3',
                          defaultMessage: 'Call {phoneNumber}',
                          description: 'Button label to call support with phone number',
                        },
                        { phoneNumber: currentContact.defaultContact.humanReadable },
                      )}
                    </TextButton>
                    {callbacksFeatureEnabled && (
                      <TextButton kind="secondary" fit="flex" onClick={onCallbackButtonClick}>
                        {intl.formatMessage({
                          id: 'B4gcrc2d3B',
                          defaultMessage: 'Request a callback',
                          description: 'Button text to request agent callback for multi-room booking',
                        })}
                      </TextButton>
                    )}
                  </Group>
                </Sheet>
              )}
            </div>
          </Group>
        </div>
        <div>
          {showBNBL && <OfferPageBNBLcta onOpenModal={onOpenBnblModal} direction="horizontal" desktopDirection="vertical" />}
        </div>
      </Group>
    </FooterContainer>
  )
}

type MappedProps = Pick<Props,
  | 'offer'
  | 'rooms'
  | 'checkIn'
  | 'isFlightsSelected'
  | 'multiRoomPackages'
  | 'multiRoomPackageTotals'
  | 'isLoadingByRoom'
  | 'calendarsToBeFetchedByRoom'
  | 'canRedeemLuxPlusBenefits'
  | 'baseBnblPackageViews'
  | 'priceMode'
  | 'priceIsLuxPlus'
  | 'charterBundleRoute'
  | 'duration'
>

export default withOfferPageAndReduxState((state, appState): MappedProps => ({
  offer: state.offer as App.HotelOffer,
  rooms: state.rooms ?? EmptyArray,
  checkIn: state.checkInDate,
  isFlightsSelected: state.flights,
  multiRoomPackages: getActivePackagesByRoom(state, appState),
  multiRoomPackageTotals: getMultiRoomPackageTotals(state, appState),
  isLoadingByRoom: getIsLoadingByRoom(state, appState),
  calendarsToBeFetchedByRoom: getCalendarsToBeFetchedByRoom(state, appState),
  canRedeemLuxPlusBenefits: getCanRedeemLuxPlusBenefits(appState),
  baseBnblPackageViews: getBnblPackageViews(state),
  priceMode: state.priceMode,
  priceIsLuxPlus: getCanViewLuxPlusBenefits(appState),
  charterBundleRoute: state.charterBundleRoute,
  duration: state.duration,
}), MultipleRoomsIndividualRoomsFooter)
